import type { FeatureContext } from './featureContext';
import type { EntityId } from '../sim/entity';
import type { ArenaFxEvent } from '../sim/arena';
import type { SphereEffect } from '../sim/sphereDuel';
import { initSphereHud, type SphereHotbarAction } from '../sphereHud';
import { isPvpMenuTarget } from './pvpTarget';
import { spellCue } from '../audio/combatCue';
import { SphereCommandTracker, type SphereSequencedAction } from '../sphereCommands';
import { SPHERE_SPELLS } from '../sim/sphereDuel';
import { SPHERE_SLOT_ACTIONS } from '../input/keybindings';

const SPHERE_DAMAGE_COLOR = 0xe2534a;
const SPHERE_HEAL_COLOR = 0x62d66f;
const SPHERE_MANA_COLOR = 0x5da9ff;
const SPHERE_STATUS_COLOR = 0x83d45b;

function sphereFx(ctx: FeatureContext, effect: SphereEffect): void {
  if (ctx.ui.hidden) return;
  const renderer = ctx.renderer();
  if (!renderer) return;
  const tile = (entity: EntityId): { x: number; y: number } | undefined => {
    const position = ctx.world()?.get(entity)?.position;
    return position ? { x: position.x, y: position.y } : undefined;
  };
  if (effect.kind === 'damage') {
    const from = tile(effect.source);
    const to = tile(effect.target);
    let fx: ArenaFxEvent | undefined;
    if (from && to && effect.spell === 'magic-arrow') {
      fx = {
        kind: 'projectile',
        projectile: 'arrow',
        sizeTier: 0,
        from,
        to,
        target: effect.target,
        damage: effect.amount,
        gen: 0,
      };
    } else if (to && effect.spell === 'flame-strike') {
      fx = {
        kind: 'flame-strike',
        at: to,
        radius: 0,
        damage: effect.amount,
        targets: [effect.target],
        gen: 0,
      };
    }
    if (fx) renderer.playVolleyFx([fx], SPHERE_DAMAGE_COLOR);
    else if (effect.spell === 'lightning') {
      renderer.playImpactFx(effect.target, 'lightning', SPHERE_DAMAGE_COLOR);
      renderer.showCombatText(effect.target, String(effect.amount), SPHERE_DAMAGE_COLOR);
    } else if (effect.spell === 'poison') {
      renderer.showCombatText(effect.target, `${effect.amount} poison`, SPHERE_STATUS_COLOR);
    } else {
      renderer.showCombatText(effect.target, String(effect.amount), SPHERE_DAMAGE_COLOR);
    }
    if (
      effect.spell === 'magic-arrow'
      || effect.spell === 'flame-strike'
      || effect.spell === 'lightning'
    ) {
      const cue = spellCue(effect.spell, 1);
      if (cue) ctx.audio.play(cue);
    }
  } else if (effect.kind === 'poison') {
    renderer.playImpactFx(effect.target, 'poison', SPHERE_STATUS_COLOR);
    renderer.showCombatText(effect.target, effect.refreshed ? 'Poison renewed' : 'Poisoned', SPHERE_STATUS_COLOR);
    const cue = spellCue('poison', 1);
    if (cue) ctx.audio.play(cue);
  } else if (effect.kind === 'heal') {
    if (effect.source === 'spell') {
      renderer.playImpactFx(effect.player, 'greater-heal', SPHERE_HEAL_COLOR);
      const cue = spellCue('greater-heal', 1);
      if (cue) ctx.audio.play(cue);
    }
    renderer.showCombatText(
      effect.player,
      effect.amount > 0 ? `+${effect.amount}` : 'No effect',
      SPHERE_HEAL_COLOR,
    );
  } else if (effect.kind === 'cure') {
    renderer.playImpactFx(effect.player, 'cure', SPHERE_STATUS_COLOR);
    renderer.showCombatText(effect.player, effect.removed ? 'Cured' : 'Not poisoned', SPHERE_STATUS_COLOR);
    const cue = spellCue('cure', 1);
    if (cue) ctx.audio.play(cue);
  } else if (effect.kind === 'potion' && effect.potion === 'mana') {
    renderer.showCombatText(effect.player, `+${effect.amount} mana`, SPHERE_MANA_COLOR);
  }
}

export interface SphereInstallDeps {
  initHud?: typeof initSphereHud;
}

/** Install the separate challenge, private HUD, and hotbar for Sphere Duel. */
export function installSphere(ctx: FeatureContext, deps: SphereInstallDeps = {}): void {
  let readySent = false;
  let active = false;
  let currentMatchId: number | undefined;
  let lastVitals = '';
  let readinessToken = 0;
  const commands = new SphereCommandTracker();
  const suppressPresentation = new Set<string>();
  const commandKey = (matchId: number, sequence: number): string => `${matchId}:${sequence}`;

  const present = (action: SphereSequencedAction): void => {
    const entity = ctx.playerId();
    if (entity === undefined) return;
    const renderer = ctx.renderer();
    if (action.kind === 'cast') {
      const spell = SPHERE_SPELLS[action.spell];
      hud.effect({
        kind: 'cast-start',
        player: entity,
        spell: action.spell,
        words: spell.words,
      });
      const facing = ctx.world()?.get(entity)?.position?.facing;
      if (facing !== undefined) renderer?.playActionAnimation?.(entity, 'cast', facing);
      renderer?.showSpeech?.(entity, spell.words, 0xe5d28b);
    } else if (action.kind === 'potion') {
      hud.acknowledge?.('Using potion...');
    } else {
      hud.acknowledge?.('Cancelling cast...');
    }
  };

  const send = (action: SphereHotbarAction): void => {
    const entity = ctx.playerId();
    if (entity === undefined) return;
    if (action.kind === 'cast') {
      const pending = commands.allocate(action);
      if (!pending) return;
      present(action);
      ctx.client()?.sendCommand({
        kind: 'SphereCast',
        entity,
        spell: action.spell,
        matchId: pending.matchId,
        sequence: pending.sequence,
      });
    } else if (action.kind === 'potion') {
      const pending = commands.allocate(action);
      if (!pending) return;
      present(action);
      ctx.client()?.sendCommand({
        kind: 'SpherePotion',
        entity,
        potion: action.potion,
        matchId: pending.matchId,
        sequence: pending.sequence,
      });
    } else if (action.kind === 'cancel') {
      const pending = commands.allocate(action);
      if (!pending) return;
      present(action);
      ctx.client()?.sendCommand({
        kind: 'SphereCancel',
        entity,
        matchId: pending.matchId,
        sequence: pending.sequence,
      });
    } else if (action.kind === 'rematch') {
      ctx.client()?.sendCommand({ kind: 'SphereRematch', entity });
    } else {
      ctx.client()?.sendCommand({ kind: 'SphereExit', entity });
    }
  };
  const hud = (deps.initHud ?? initSphereHud)(send, ctx.ui, ctx.bindings);

  // The hotbar hotkeys are registry actions (sphere-slot-1..8, default Digit1-8,
  // settings-overhaul) so they dispatch through main.ts's single hotkey path and
  // rebind in Settings like any other action. The hud gates each press on hotbar
  // visibility + slot availability, so a bound key outside a duel is a no-op.
  SPHERE_SLOT_ACTIONS.forEach((action, slot) => {
    ctx.actions.register(action, { down: () => hud.pressSlot?.(slot) });
  });

  const start = (target: EntityId): void => {
    const opponentName = ctx.world()?.get(target)?.renderable?.name ?? 'that player';
    const gold = ctx.state.gold();
    ctx.state.duelUi.promptWager({
      opponentName,
      matchNoun: 'a Sphere Duel',
      maxGold: gold.durable ? gold.amount : 0,
      canStake: gold.durable,
      onConfirm: (wager) => {
        const entity = ctx.playerId();
        if (entity !== undefined) {
          ctx.client()?.sendCommand({ kind: 'SphereChallenge', entity, opponent: target, wager });
        }
      },
    });
  };

  ctx.state.onTargetPick('sphere', start);
  ctx.chatCommands.intercept((text) => {
    if (text.trim().toLowerCase() !== '/sphere') return false;
    ctx.state.armTargeting('sphere');
    return true;
  });
  ctx.menu.addEntityMenu((target) => {
    if (!isPvpMenuTarget(ctx.world(), ctx.playerId(), target.id, target.name)) return null;
    return { items: [{ label: `Sphere duel ${target.name}`, onSelect: () => start(target.id) }] };
  });

  ctx.events.on('connection', () => {
    readySent = false;
    active = false;
    currentMatchId = undefined;
    lastVitals = '';
    readinessToken += 1;
    commands.reset();
    suppressPresentation.clear();
    hud.hide();
    ctx.renderer()?.setSphereVitals({ active: false });
    ctx.music.setArena(false);
    // A mid-match disconnect never sends the final inactive state, so release the
    // sphere warm sound set with the fresh connection too (issue #626).
    void ctx.audio.warmMode(null);
  });
  ctx.events.on('spherePrompt', (msg) => {
    if (!msg.open) {
      ctx.state.duelUi.dismissChallenge();
      return;
    }
    ctx.state.duelUi.showChallenge({
      challengerName: msg.challengerName,
      matchNoun: 'a Sphere Duel',
      wager: msg.wager,
      onAccept: () => {
        const entity = ctx.playerId();
        if (entity !== undefined) ctx.client()?.sendCommand({ kind: 'SphereAccept', entity });
      },
      onDecline: () => {
        const entity = ctx.playerId();
        if (entity !== undefined) ctx.client()?.sendCommand({ kind: 'SphereDecline', entity });
      },
    });
  });
  ctx.events.on('sphereState', (msg) => {
    const matchId = msg.matchId ?? currentMatchId ?? 0;
    if (currentMatchId !== matchId) {
      currentMatchId = matchId;
      readySent = false;
      suppressPresentation.clear();
    }
    commands.sync(matchId, msg.nextCommandSequence ?? 1);
    hud.render(msg);
    const vitals = {
      active: msg.active,
      local: { id: msg.youId, health: msg.health, mana: msg.mana, poisoned: msg.poisoned },
      opponent: { id: msg.opponentId, health: msg.opponentHealth, poisoned: msg.opponentPoisoned },
    };
    const vitalsKey = JSON.stringify(vitals);
    if (vitalsKey !== lastVitals) {
      lastVitals = vitalsKey;
      ctx.renderer()?.setSphereVitals(vitals);
    }
    if (msg.active !== active) {
      active = msg.active;
      ctx.music.setArena(active);
      // Warm Sphere Duel's critical cues on the match edge (issue #626). The
      // first active push lands in the `loading` ready-handshake phase, so the
      // spell/fizzle set decodes before combat can need it; leaving releases it.
      if (!active) void ctx.audio.warmMode(null);
    }
    if (msg.phase === 'loading' && !readySent) {
      readySent = true;
      const token = ++readinessToken;
      const renderer = ctx.renderer();
      const audioLoad = ctx.audio.load?.();
      const audioReady = audioLoad
        ? audioLoad.then(() => ctx.audio.warmMode('sphere'))
        : ctx.audio.warmMode('sphere');
      void Promise.allSettled([
        audioReady,
        hud.ready?.() ?? Promise.resolve(),
        renderer?.prepareSphereAssets?.([msg.youId, msg.opponentId]) ?? Promise.resolve(),
      ]).then(() => {
        if (token !== readinessToken || currentMatchId !== matchId) return;
        const entity = ctx.playerId();
        if (entity !== undefined) ctx.client()?.sendCommand({ kind: 'SphereReady', entity });
      });
    }
    if (msg.phase === 'result') readySent = false;
    if (!msg.active) {
      readySent = false;
      readinessToken += 1;
      commands.reset();
      currentMatchId = undefined;
      lastVitals = '';
      hud.hide();
    }
  });
  ctx.events.on('sphereCommandResult', (msg) => {
    const pending = commands.settle(msg.matchId, msg.sequence);
    if (!pending) return;
    if (msg.accepted && pending.action.kind === 'cast') {
      suppressPresentation.add(commandKey(msg.matchId, msg.sequence));
    } else if (!msg.accepted) {
      suppressPresentation.add(commandKey(msg.matchId, msg.sequence));
      // A pre-cast line-of-sight block never began a cast: the server delivers a
      // caster-only "not in line of sight" advisory to chat instead, so present no
      // fizzle. (A mid-cast LOS loss still arrives as a SphereEvent fizzle below.)
      if (msg.reason !== 'line-of-sight') {
        hud.effect({
          kind: 'fizzle',
          player: ctx.playerId()!,
          ...(pending.action.kind === 'cast' ? { spell: pending.action.spell } : {}),
          reason: msg.reason ?? 'stale-command',
        });
        ctx.audio.play({ sound: 'sphere-fizzle', volume: 1 });
      }
    }
  });
  ctx.events.on('sphereEvent', (msg) => {
    if (
      msg.command
      && (msg.effect.kind === 'cast-start' || msg.effect.kind === 'fizzle')
      && suppressPresentation.delete(commandKey(msg.command.matchId, msg.command.sequence))
    ) return;
    hud.effect(msg.effect);
    sphereFx(ctx, msg.effect);
    if (msg.effect.kind === 'fizzle') {
      ctx.audio.play({ sound: 'sphere-fizzle', volume: 1 });
    }
  });
}
